import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit
} from 'firebase/firestore';
import { db } from '../../config/firebase';

import { normalizeDepartment } from '../../utils/departmentVisibility';

// Helper: Get collection name
const getCollectionName = (month, year) => {
    const m = String(month).padStart(2, '0');
    return `attendance_${year}_${m}`;
};

// Date range and matching helpers
const parseDate = (val) => {
    if (!val) return null;
    if (val.toDate && typeof val.toDate === 'function') {
        const d = val.toDate();
        d.setHours(0, 0, 0, 0);
        return d;
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
    }
    return null;
};

const getDatesInRange = (startStr, endStr) => {
    const dates = [];
    let current = new Date(startStr);
    const end = new Date(endStr);

    while (current <= end) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const getCollectionsForRange = (startStr, endStr) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const collections = [];

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        const today = new Date();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        return [`attendance_${today.getFullYear()}_${m}`];
    }

    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);

    while (current <= last) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        collections.push(`attendance_${year}_${month}`);
        current.setMonth(current.getMonth() + 1);
    }

    return collections;
};

const getLeaveTypeLabel = (type) => {
    const labels = {
        sick_leave: 'Sick Leave',
        emergency_leave: 'Emergency Leave',
        casual_leave: 'Casual Leave',
        paid_leave: 'Paid Leave',
    };
    return labels[type] || type;
};

const isDepartmentMatch = (itemDept, selectedDept, empId, employeeDeptMap, departmentsList = []) => {
    if (!selectedDept) return true;

    const targetDept = selectedDept.toUpperCase().trim();
    const targetNorm = normalizeDepartment(selectedDept);

    // Find name corresponding to this code (or vice-versa) from departmentsList
    const deptInfo = departmentsList.find(d =>
        (d.code || '').toUpperCase().trim() === targetDept ||
        (d.name || '').toUpperCase().trim() === targetDept
    );
    const targetCode = deptInfo ? (deptInfo.code || '').toUpperCase().trim() : targetDept;
    const targetName = deptInfo ? (deptInfo.name || '').toUpperCase().trim() : targetDept;

    const checkMatch = (dept) => {
        if (!dept) return false;
        const d = dept.toUpperCase().trim();
        const dNorm = normalizeDepartment(dept);
        if (targetNorm && dNorm && targetNorm === dNorm) return true;
        return d === targetCode || d === targetName || d.includes(targetDept) || targetDept.includes(d);
    };

    // Check item's department field
    if (checkMatch(itemDept)) return true;

    // Check employee's department mapping
    if (empId) {
        const mappedDept = employeeDeptMap[empId] || employeeDeptMap[String(empId)];
        if (checkMatch(mappedDept)) return true;
    }

    return false;
};

/**
 * Get dashboard statistics
 * @param {Object} options - Options for filtering
 * @param {string} options.employeeId - Employee ID (Firebase UID) for employee-specific stats
 * @param {string} options.employeeCode - Employee code for attendance matching
 * @param {string} options.employeeName - Employee name for fallback matching
 * @param {string} options.role - User role (admin, hr, employee)
 * @param {string} options.department - Department filter
 */
export const getDashboardStats = async (options = {}) => {
    try {
        const { employeeId, employeeCode, employeeName, role, department, startDate, endDate } = options;
        const isAdmin = role === 'admin' || role === 'hr' || role === 'superadmin';
        const isEmployee = !isAdmin;

        console.log('[DashboardService] Fetching dashboard stats...', { isEmployee, isAdmin, employeeId, employeeCode, employeeName, department, startDate, endDate });

        if (isEmployee && employeeId) {
            return await getEmployeeDashboardStats(employeeId, employeeCode, employeeName);
        }

        // 1. Get all departments to build mapping
        let departmentsList = [];
        try {
            const deptsRef = collection(db, 'departments');
            const deptsSnapshot = await getDocs(deptsRef);
            departmentsList = deptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.warn('[DashboardService] Failed to load departments:', e);
        }

        // 2. Get all employees
        const usersRef = collection(db, 'employees');
        const usersSnapshot = await getDocs(usersRef);

        const employeeDeptMap = {};
        const activeEmployeeIds = new Set();
        const departmentCounts = {};
        let totalEmployees = 0;
        let activeCount = 0;
        let probationCount = 0;
        let newJoiningsCount = 0;
        let resignationsCount = 0;
        let incompleteProfilesCount = 0;

        const today = new Date();
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

        usersSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.isDeleted === true) return;

            const status = (data.status || '').toLowerCase();
            const isInactive = status === 'inactive' || data.isActive === false || status === 'resigned' || status === 'terminated' || status === 'exited';

            if (isInactive) {
                resignationsCount++;
                return; // Exclude inactive employees from all active dashboard stats
            }

            activeCount++;
            if (status === 'probation') probationCount++;

            const hasBasicInfo = (data.firstName && data.lastName && data.email) || (data.personalInfo?.firstName && data.personalInfo?.email) || (data.personal?.firstName && data.personal?.email);
            const hasJobInfo = (data.job?.department || data.jobDetails?.department || data.department) && (data.job?.designation || data.jobDetails?.designation || data.jobDetails?.position || data.designation);
            const hasDocuments = data.documents && Array.isArray(data.documents) && data.documents.length > 0;
            const isComplete = data.isProfileComplete === true || (hasBasicInfo && hasJobInfo && hasDocuments);

            const empDept = data.job?.department || data.department || data.jobDetails?.department || 'Unassigned';
            const empCode = data.job?.employeeCode || data.employeeCode || data.employeeId || data.jobDetails?.employeeCode || doc.id;

            employeeDeptMap[empCode] = empDept;
            employeeDeptMap[String(empCode)] = empDept;
            employeeDeptMap[doc.id] = empDept;
            if (data.employeeId) employeeDeptMap[data.employeeId] = empDept;
            if (data.authUserId) employeeDeptMap[data.authUserId] = empDept;
            if (data.uid) employeeDeptMap[data.uid] = empDept;

            activeEmployeeIds.add(doc.id);
            activeEmployeeIds.add(empCode);
            activeEmployeeIds.add(String(empCode));
            if (data.employeeId) activeEmployeeIds.add(String(data.employeeId));
            if (data.authUserId) activeEmployeeIds.add(String(data.authUserId));
            if (data.uid) activeEmployeeIds.add(String(data.uid));

            if (!isDepartmentMatch(empDept, department, doc.id, employeeDeptMap, departmentsList)) {
                return;
            }

            if (!isComplete) incompleteProfilesCount++;

            if (data.job?.joiningDate || data.joiningDate || data.jobDetails?.joinDate) {
                const rawJoinDate = data.job?.joiningDate || data.joiningDate || data.jobDetails?.joinDate || data.jobDetails?.joiningDate;
                const joinDate = rawJoinDate?.toDate ? rawJoinDate.toDate() : new Date(rawJoinDate);
                if (joinDate >= currentMonthStart) newJoiningsCount++;
            }

            totalEmployees++;
            departmentCounts[empDept] = (departmentCounts[empDept] || 0) + 1;
        });

        const byDepartment = Object.entries(departmentCounts).map(([name, count]) => ({
            department: name,
            count
        }));

        // 3. Resolve date range
        const todayStr = today.toISOString().split('T')[0];
        const startStr = startDate || todayStr;
        const endStr = endDate || todayStr;
        const startRange = parseDate(startStr);
        const endRange = parseDate(endStr);
        const dates = getDatesInRange(startStr, endStr);

        // 4. Fetch WRMS snapshots in parallel
        const [leavesSnapshot, earlyLeavesSnapshot, gatePassesSnapshot, extraWorkSnapshot] = await Promise.all([
            getDocs(collection(db, 'wrms_leave_requests')),
            getDocs(collection(db, 'wrms_early_leave_requests')),
            getDocs(collection(db, 'wrms_gate_pass_requests')),
            getDocs(collection(db, 'wrms_extra_work_entries'))
        ]);

        const processWrmsRequests = (snapshot, type) => {
            let pending = 0;
            let approved = 0;
            let rejected = 0;
            const list = [];

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const empId = data.employeeId;

                // Skip requests from inactive employees
                if (activeEmployeeIds.size > 0 && empId && !activeEmployeeIds.has(empId) && !activeEmployeeIds.has(String(empId))) {
                    return;
                }

                const empDept = data.department || employeeDeptMap[empId] || employeeDeptMap[String(empId)] || '';

                if (!isDepartmentMatch(empDept, department, empId, employeeDeptMap, departmentsList)) {
                    return;
                }

                const status = (data.status || '').toLowerCase();
                if (status === 'pending') pending++;
                else if (status === 'approved') approved++;
                else if (status === 'rejected') rejected++;

                list.push({
                    id: docSnap.id,
                    employeeName: data.employeeName || 'Unknown',
                    requestId: data.requestId || docSnap.id,
                    status,
                    submittedAt: data.submittedAt,
                    dateString: data.dateString || data.fromDate || data.date || data.startDate || ''
                });
            });

            // Sort list by submittedAt descending
            list.sort((a, b) => {
                const ta = a.submittedAt?.toMillis?.() || 0;
                const tb = b.submittedAt?.toMillis?.() || 0;
                return tb - ta;
            });

            return { pending, approved, rejected, list };
        };

        const leavesData = processWrmsRequests(leavesSnapshot, 'leave');
        const earlyLeavesData = processWrmsRequests(earlyLeavesSnapshot, 'early_leave');
        const gatePassesData = processWrmsRequests(gatePassesSnapshot, 'gate_pass');
        const extraWorkData = processWrmsRequests(extraWorkSnapshot, 'extra_work');

        // Build currentlyOnLeave and leaveDaysMap using leavesSnapshot (maintaining original logic)
        let pendingLeaves = leavesData.pending;
        let rejectedLeaves = leavesData.rejected;
        let approvedLeaves = leavesData.approved;
        const currentlyOnLeave = [];
        const uniqueOnLeaveEmployees = new Set();

        const leaveDaysMap = {}; // Maps dString -> Set of employeeIds on leave
        dates.forEach(d => { leaveDaysMap[d] = new Set(); });

        leavesSnapshot.forEach((doc) => {
            const data = doc.data();
            const empId = data.employeeId;

            // Skip requests from inactive employees
            if (activeEmployeeIds.size > 0 && empId && !activeEmployeeIds.has(empId) && !activeEmployeeIds.has(String(empId))) {
                return;
            }

            const empDept = data.department || employeeDeptMap[empId] || employeeDeptMap[String(empId)] || '';

            if (!isDepartmentMatch(empDept, department, empId, employeeDeptMap, departmentsList)) {
                return;
            }

            const leaveStart = parseDate(data.fromDate || data.startDate);
            const leaveEnd = parseDate(data.toDate || data.endDate);
            if (!leaveStart || !leaveEnd) return;

            const status = (data.status || '').toLowerCase();
            const overlaps = (leaveStart <= endRange && leaveEnd >= startRange);

            if (overlaps && status === 'approved') {
                if (!uniqueOnLeaveEmployees.has(empId)) {
                    uniqueOnLeaveEmployees.add(empId);
                    currentlyOnLeave.push({
                        name: data.employeeName || 'Unknown',
                        leaveType: getLeaveTypeLabel(data.leaveType || 'leave'),
                        endDate: data.toDate || data.endDate || ''
                    });
                }

                // Map this leave to specific days in our range
                let d = new Date(leaveStart);
                while (d <= leaveEnd) {
                    const dStr = d.toISOString().split('T')[0];
                    if (leaveDaysMap[dStr]) {
                        leaveDaysMap[dStr].add(empId);
                    }
                    d.setDate(d.getDate() + 1);
                }
            }
        });

        // 5. Get attendance
        const collections = getCollectionsForRange(startStr, endStr);
        const attendanceRecords = [];

        for (const colName of collections) {
            try {
                const snap = await getDocs(collection(db, colName));
                snap.forEach(doc => {
                    const record = doc.data();
                    const empId = record.employeeId;

                    // Skip attendance from inactive employees
                    if (activeEmployeeIds.size > 0 && empId && !activeEmployeeIds.has(empId) && !activeEmployeeIds.has(String(empId))) {
                        return;
                    }

                    const empDept = employeeDeptMap[empId] || employeeDeptMap[String(empId)] || '';

                    if (!isDepartmentMatch(empDept, department, empId, employeeDeptMap, departmentsList)) {
                        return;
                    }
                    if (record.dateString >= startStr && record.dateString <= endStr) {
                        attendanceRecords.push(record);
                    }
                });
            } catch (e) {
                console.warn(`[DashboardService] Attendance collection ${colName} not available:`, e);
            }
        }

        // Build daily stats mapping
        const dailyStats = {};
        dates.forEach(d => {
            dailyStats[d] = { present: 0, late: 0, leave: leaveDaysMap[d].size };
        });

        attendanceRecords.forEach(record => {
            const dStr = record.dateString;
            if (!dStr || !dailyStats[dStr]) return;

            const status = record.status?.toLowerCase() || '';
            if (status === 'present' || status === 'p' || status === 'late' || status === 'half-day') {
                dailyStats[dStr].present++;
            }
            if (status === 'late' || record.isLate === true) {
                dailyStats[dStr].late++;
            }
        });

        // Compute averages across range
        let totalPresent = 0;
        let totalAbsent = 0;
        let totalLeave = 0;
        let totalLate = 0;

        dates.forEach(d => {
            const s = dailyStats[d];
            const abs = Math.max(0, totalEmployees - s.present - s.leave);
            totalPresent += s.present;
            totalAbsent += abs;
            totalLeave += s.leave;
            totalLate += s.late;
        });

        const numDays = dates.length || 1;
        const presentToday = Math.round(totalPresent / numDays);
        const absentToday = Math.round(totalAbsent / numDays);
        const onLeaveToday = uniqueOnLeaveEmployees.size;
        const lateToday = Math.round(totalLate / numDays);

        // Generate attendance trend
        const trend = [];
        // Show last 30 days of the range for the trend chart
        const trendDates = dates.slice(-30);
        trendDates.forEach(d => {
            const s = dailyStats[d] || { present: 0, leave: 0 };
            const abs = Math.max(0, totalEmployees - s.present - s.leave);
            trend.push({
                date: d,
                present: s.present,
                absent: abs
            });
        });

        return {
            success: true,
            data: {
                totalEmployees,
                totalDepartments: byDepartment.length,
                pendingLeaves,
                approvedLeaves,
                rejectedLeaves,
                wrmsStats: {
                    leave: leavesData,
                    early_leave: earlyLeavesData,
                    gate_pass: gatePassesData,
                    extra_work: extraWorkData
                },
                onLeaveToday,
                presentToday,
                absentToday,
                lateToday,
                active: activeCount,
                probation: probationCount,
                newJoinings: newJoiningsCount,
                resignations: resignationsCount,
                incompleteProfiles: incompleteProfilesCount,
                byDepartment,
                currentlyOnLeave,
                attendanceTrend: trend
            }
        };
    } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        throw new Error(error.message || 'Failed to fetch dashboard statistics');
    }
};

/**
 * Get recent activities
 */
export const getRecentActivities = async (limitCount = 10) => {
    try {
        const activities = [];

        try {
            const q = query(collection(db, 'wrms_leave_requests'), orderBy('submittedAt', 'desc'), limit(limitCount));
            const leavesSnapshot = await getDocs(q);

            leavesSnapshot.forEach((doc) => {
                const data = doc.data();
                activities.push({
                    id: doc.id,
                    type: 'leave',
                    message: `${data.employeeName || 'An employee'} applied for ${getLeaveTypeLabel(data.leaveType || 'leave')}`,
                    timestamp: data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt || new Date().toISOString(),
                    status: data.status
                });
            });
        } catch (e) {
            console.warn("Error fetching recent leaves for activity", e);
        }

        // Sort by timestamp
        activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return {
            success: true,
            data: activities.slice(0, limitCount)
        };
    } catch (error) {
        console.error('Error fetching recent activities:', error);
        return { success: true, data: [] }; // Return empty on error to prevent crash
    }
};

/**
 * Get attendance overview for current month
 * @param {Object} options - Options for filtering
 * @param {string} options.department - Department filter
 */
export const getAttendanceOverview = async (options = {}) => {
    try {
        const { department, startDate, endDate } = options;
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];

        const startStr = startDate || todayStr;
        const endStr = endDate || todayStr;

        const overview = {
            present: 0,
            absent: 0,
            leave: 0,
            halfDay: 0,
            late: 0
        };

        // 1. Get all departments to build mapping
        let departmentsList = [];
        try {
            const deptsRef = collection(db, 'departments');
            const deptsSnapshot = await getDocs(deptsRef);
            departmentsList = deptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.warn('[DashboardService] Failed to load departments:', e);
        }

        // 2. Build employee department map and active employee set
        const employeeDeptMap = {};
        const activeEmployeeIds = new Set();

        const usersRef = collection(db, 'employees');
        const usersSnapshot = await getDocs(usersRef);
        usersSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.isDeleted === true) return;

            // Check if active - strictly exclude inactive
            const isInactive = (data.status || '').toLowerCase() === 'inactive' || data.isActive === false;
            if (isInactive) return;

            const empDept = data.job?.department || data.department || '';
            const empCode = data.job?.employeeCode || data.employeeCode || data.employeeId || doc.id;

            // Store department mapping
            employeeDeptMap[empCode] = empDept;
            employeeDeptMap[String(empCode)] = empDept;
            employeeDeptMap[doc.id] = empDept;
            if (data.employeeId) employeeDeptMap[data.employeeId] = empDept;

            // Store active employee IDs
            activeEmployeeIds.add(doc.id);
            activeEmployeeIds.add(empCode);
            activeEmployeeIds.add(String(empCode));
            if (data.employeeId) activeEmployeeIds.add(String(data.employeeId));
            if (data.authUserId) activeEmployeeIds.add(String(data.authUserId));
            if (data.uid) activeEmployeeIds.add(String(data.uid));
        });

        // 3. Query all collections spanned by the date range
        const collections = getCollectionsForRange(startStr, endStr);
        const attendanceRecords = [];

        for (const colName of collections) {
            try {
                const snap = await getDocs(collection(db, colName));
                snap.forEach(doc => {
                    const record = doc.data();
                    const empId = record.employeeId;

                    // Skip attendance from inactive employees
                    if (!activeEmployeeIds.has(empId) && !activeEmployeeIds.has(String(empId))) return;

                    const empDept = employeeDeptMap[empId] || employeeDeptMap[String(empId)] || '';

                    // Apply department filter if specified
                    if (!isDepartmentMatch(empDept, department, empId, employeeDeptMap, departmentsList)) {
                        return;
                    }

                    if (record.dateString >= startStr && record.dateString <= endStr) {
                        attendanceRecords.push(record);
                    }
                });
            } catch (e) {
                console.warn(`[DashboardService] Attendance collection ${colName} not available in overview:`, e);
            }
        }

        // Aggregate attendance statistics
        attendanceRecords.forEach((record) => {
            const status = record.status?.toLowerCase() || 'absent';

            if (status === 'present' || status === 'p') {
                overview.present++;
            } else if (status === 'absent' || status === 'a') {
                overview.absent++;
            } else if (status === 'leave' || status === 'l' || status === 'on leave') {
                overview.leave++;
            } else if (status === 'half-day' || status === 'half day' || status === 'hd') {
                overview.halfDay++;
            } else if (status === 'late') {
                overview.late++;
                overview.present++; // Late is still present
            }
        });

        return {
            success: true,
            data: overview
        };
    } catch (error) {
        console.error('Error fetching attendance overview:', error);
        throw new Error(error.message || 'Failed to fetch attendance overview');
    }
};

/**
 * Get employee-specific dashboard stats
 * @param {string} employeeId - Firebase UID or employee code
 * @param {string} employeeCode - Employee code (optional, for attendance matching)
 * @param {string} employeeName - Employee name (optional, for fallback matching)
 */
const getEmployeeDashboardStats = async (employeeId, employeeCode = '', employeeName = '') => {
    try {
        console.log('[DashboardService] Fetching employee-specific stats for:', { employeeId, employeeCode, employeeName });

        const today = new Date();
        const month = today.getMonth() + 1;
        const year = today.getFullYear();
        const collectionName = getCollectionName(month, year);

        let daysPresent = 0;
        let leavesTaken = 0;
        let pendingLeaves = 0;

        // Prepare matching values (lowercase for comparison)
        const matchIds = [employeeId, employeeCode, String(employeeCode)]
            .filter(Boolean)
            .map(id => String(id).toLowerCase());
        const matchName = (employeeName || '').toLowerCase().trim();

        console.log('[DashboardService] Matching IDs:', matchIds, 'Name:', matchName);

        // Get employee's attendance for current month
        try {
            const attendanceRef = collection(db, collectionName);
            const attendanceSnapshot = await getDocs(attendanceRef);

            attendanceSnapshot.forEach((doc) => {
                const data = doc.data();
                const recordEmpId = String(data.employeeId || '').toLowerCase();
                const recordEmpName = (data.employeeName || '').toLowerCase().trim();

                // Match by ID or name
                const idMatch = matchIds.some(id => recordEmpId === id || recordEmpId.includes(id) || id.includes(recordEmpId));
                const nameMatch = matchName && recordEmpName && (recordEmpName.includes(matchName) || matchName.includes(recordEmpName));

                if (idMatch || nameMatch) {
                    const status = (data.status || '').toLowerCase();
                    if (status === 'present' || status === 'p' || status === 'half-day' || status === 'late') {
                        daysPresent++;
                    }
                }
            });
            console.log('[DashboardService] Days present:', daysPresent);
        } catch (e) {
            console.warn('Could not fetch attendance:', e);
        }

        // Get employee's leaves - fetch all and filter client-side for flexibility
        try {
            const leavesRef = collection(db, 'wrms_leave_requests');
            const leavesSnapshot = await getDocs(leavesRef);

            leavesSnapshot.forEach((doc) => {
                const data = doc.data();
                const leaveEmpId = String(data.employeeId || '').toLowerCase();
                const leaveEmpName = (data.employeeName || '').toLowerCase().trim();

                // Match by ID or name
                const idMatch = matchIds.some(id => leaveEmpId === id || leaveEmpId.includes(id) || id.includes(leaveEmpId));
                const nameMatch = matchName && leaveEmpName && (leaveEmpName.includes(matchName) || matchName.includes(leaveEmpName));

                if (idMatch || nameMatch) {
                    const status = (data.status || '').toLowerCase();
                    if (status === 'approved') {
                        leavesTaken++;
                    } else if (status === 'pending') {
                        pendingLeaves++;
                    }
                }
            });
            console.log('[DashboardService] Leaves taken:', leavesTaken, 'Pending:', pendingLeaves);
        } catch (e) {
            console.warn('Could not fetch leaves:', e);
        }

        // Default leave balance (can be customized per company policy)
        const totalLeaveBalance = 20;
        const leaveBalance = totalLeaveBalance - leavesTaken;

        return {
            success: true,
            data: {
                daysPresent,
                leavesTaken,
                leaveBalance,
                pendingLeaves,
                // These are for employee view
                totalLeaveBalance
            }
        };
    } catch (error) {
        console.error('Error fetching employee dashboard stats:', error);
        return {
            success: true,
            data: {
                daysPresent: 0,
                leavesTaken: 0,
                leaveBalance: 20,
                pendingLeaves: 0
            }
        };
    }
};

const dashboardService = {
    getDashboardStats,
    getRecentActivities,
    getAttendanceOverview,
    getEmployeeDashboardStats
};

export default dashboardService;
